import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import { OrgScope, Preloader } from "./providers";
import {
  LoadingSpinner,
  MessageBoxContainer,
  SpinnerContainer,
  ToastContainer,
} from "./components";
import { IdleTimerProvider } from "./providers/InactivityProvider";

import FullLayout from "./layouts/FullLayout";
import { ROUTES, CONFIG } from "./constants";
import { lazyWithReload } from "./utils/helpers";

import ConfirmEmail from "./pages/Auth/ConfirmEmail";
import SuccessfulActivateAccount from "./pages/Auth/SuccessfulActivateAccount";

import ConfirmTotp from "./pages/Auth/ConfirmTotp";
import NoAccount from "./pages/Auth/NoAccount";
import InactivityAlert from "./components/InactivityAlert";
import UnconfirmedLeads from "./pages/Auth/UnconfirmedLeads";
import Sso from "./pages/Auth/Sso";

// lazyWithReload, not React.lazy: while the chunk-reload handler in index.tsx
// has a reload committed, Vite resolves a failed import to undefined, which
// plain lazy() would crash on. See chunk-reload.helpers.ts.
const ResetPassword = lazyWithReload(() => import("./pages/Auth/ResetPassword"));
const AcceptInvite = lazyWithReload(() => import("./pages/Auth/AcceptInvite"));
const OnboardRouting = lazyWithReload(() => import("./pages/Onboard/routing"));
const CallsRouting = lazyWithReload(() => import("./pages/Calls/routing"));
const MessagesRouting = lazyWithReload(() => import("./pages/Messages/routing"));
const NumbersRouting = lazyWithReload(() => import("./pages/Numbers/routing"));
const PhoneUsersRouting = lazyWithReload(
  () => import("./pages/PhoneUsers/routing"),
);
const DeveloperRouting = lazyWithReload(
  () => import("./pages/Developer/routing"),
);
const ErrorReportingRouting = lazyWithReload(
  () => import("./pages/ErrorReporting/routing"),
);
const SettingsRouting = lazyWithReload(() => import("./pages/Settings/routing"));
const WhatsappRouting = lazyWithReload(() => import("./pages/Whatsapp/routing"));
const QuickMetricsRouting = lazyWithReload(
  () => import("./pages/QuickMetrics/routing"),
);
const DashboardRouting = lazyWithReload(
  () => import("./pages/Dashboard/routing"),
);

const theme = createTheme({
  typography: {
    fontFamily: "Montserrat, sans-serif",
  },
});

const RouteFallback = () => (
  <div role="status" aria-live="polite" className="w-full py-24 flex-center">
    <span className="sr-only">Loading page</span>
    <span aria-hidden="true">
      <LoadingSpinner />
    </span>
  </div>
);

const Pages = () => {
  const default_page = ROUTES.DASH.INDEX;

  return (
    <Preloader>
      <Suspense fallback={<RouteFallback />}>
        <Route path={ROUTES.ONBOARD.PREFIX} component={OnboardRouting} />
      </Suspense>
      <IdleTimerProvider>
        <InactivityAlert />
        <FullLayout>
          {/* Inside FullLayout so the chrome stays put while a chunk loads —
              a boundary above it would blank the sidebar and header too. */}
          <Suspense fallback={<RouteFallback />}>
            <OrgScope>
              <Switch>
                <Redirect path={ROUTES.DASHBOARD} to={default_page} />
                <Route path={ROUTES.DASH.PREFIX} component={DashboardRouting} />
                <Route
                  path={ROUTES.QUICK_METRICS.PREFIX}
                  component={QuickMetricsRouting}
                />
                <Route path={ROUTES.CALLS.PREFIX} component={CallsRouting} />
                <Route
                  path={ROUTES.MESSAGES.PREFIX}
                  component={MessagesRouting}
                />
                <Route
                  path={ROUTES.NUMBERS.PREFIX}
                  component={NumbersRouting}
                />
                <Route
                  path={ROUTES.PHONE_USERS.PREFIX}
                  component={PhoneUsersRouting}
                />
                <Route
                  path={ROUTES.DEVELOPER.PREFIX}
                  component={DeveloperRouting}
                />
                <Route
                  path={ROUTES.ERROR_REPORTING.PREFIX}
                  component={ErrorReportingRouting}
                />
                <Route
                  path={ROUTES.WHATSAPP.PREFIX}
                  component={WhatsappRouting}
                />
                <Route
                  path={ROUTES.SETTINGS.PREFIX}
                  component={SettingsRouting}
                />
                <Redirect to={ROUTES.DASHBOARD} />
              </Switch>
            </OrgScope>
          </Suspense>
        </FullLayout>
      </IdleTimerProvider>
    </Preloader>
  );
};

const App = () => (
  <ThemeProvider theme={theme}>
    <Router basename={CONFIG.BASE_PATH}>
      {/* Wraps the Switch rather than sitting inside it. react-router v5
          renders the first child whose `path` matches, and a child with no
          `path` prop matches everything — a Suspense placed among these
          routes would swallow the entire auth router. */}
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          <Route path={ROUTES.AUTH.NO_ACCOUNT} component={NoAccount} />
          <Route path={ROUTES.AUTH.CONFIRM_EMAIL} component={ConfirmEmail} />
          <Route
            path={ROUTES.AUTH.VERIFIED}
            component={SuccessfulActivateAccount}
          />
          <Route path={ROUTES.AUTH.RESET_PASSWORD} component={ResetPassword} />
          <Route path={ROUTES.AUTH.ACCEPT_INVITE} component={AcceptInvite} />
          <Route path={ROUTES.AUTH.CONFIRM_TOTP} component={ConfirmTotp} />
          <Route
            path={ROUTES.AUTH.UNCONFIRMED_LEADS}
            component={UnconfirmedLeads}
          />
          <Route path={ROUTES.AUTH.SSO} component={Sso} />
          {Pages()}
        </Switch>
      </Suspense>
    </Router>
    <ToastContainer />
    <MessageBoxContainer />
    <SpinnerContainer />
  </ThemeProvider>
);

export default App;
